import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Box, Button } from "@amzn/awsui-components-react/polaris";

import CustomMultiHybridInputAndSelect from "../Templates/components/CustomMultiHybridInputAndSelect";
import { useSuggestedLogins } from "src/hooks/useSuggestedLogins";

// Type options per role. The shared CustomMultiHybridInputAndSelect only shows
// the phonetool alias autosuggest when the selected type value is exactly
// "UPLOADER" or "APPROVER"; "*_LDAP" renders a plain text input for the group.
const TYPE_OPTIONS = {
  preparer: [
    { label: "USER", value: "UPLOADER" },
    { label: "LDAP", value: "UPLOADER_LDAP" },
  ],
  reviewer: [
    { label: "USER", value: "APPROVER" },
    { label: "LDAP", value: "APPROVER_LDAP" },
  ],
  approver: [
    { label: "USER", value: "APPROVER" },
    { label: "LDAP", value: "APPROVER_LDAP" },
  ],
};

/**
 * ag-grid popup cell editor for the Preparer / Reviewer / Approver columns.
 *
 * Renders the shared CustomMultiHybridInputAndSelect so admins get the same
 * USER/LDAP type picker, alias autosuggest, and add/remove-multiple experience
 * used in the CRA deliverables control panel. Committed value is an array of
 * `{ alias, type[, column_index] }` rows.
 *
 * cellEditorParams: { role: "preparer"|"reviewer"|"approver", columnIndex? }
 */
const RoleAliasCellEditor = forwardRef((props, ref) => {
  const role = props.role || "preparer";
  const columnIndex = props.columnIndex;
  const options = TYPE_OPTIONS[role] || TYPE_OPTIONS.preparer;

  // Working copy of the row's alias entries; CustomMultiHybridInputAndSelect
  // pushes every change back through updateValue.
  const rowsRef = useRef(
    Array.isArray(props.value) ? JSON.parse(JSON.stringify(props.value)) : []
  );
  // Force a render only to keep React state in sync; the editor reads rowsRef.
  const [, setTick] = useState(0);

  const { suggestedLogins, aliasesLoading, getSuggestedLoginsCaller } = useSuggestedLogins();

  useImperativeHandle(ref, () => ({
    // Value ag-grid stores back on the row when editing stops.
    // CustomMultiHybridInputAndSelect only surfaces a duplicate *warning*; it
    // still emits duplicate {alias, type} rows through updateValue, so dedupe
    // here as a safeguard before committing to the grid/backend.
    getValue() {
      const seen = new Set();
      return rowsRef.current
        .filter((r) => r && r.type && String(r.alias || "").trim())
        .filter((r) => {
          const key = `${String(r.alias).trim()}|${r.type}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((r) => {
          const clean = { alias: String(r.alias).trim(), type: r.type };
          if (columnIndex != null) clean.column_index = columnIndex;
          return clean;
        });
    },
    // Render as a popup anchored to the cell rather than an in-cell editor.
    isPopup() {
      return true;
    },
  }));

  const getValues = () => rowsRef.current;
  const updateValue = (_field, values) => {
    rowsRef.current = values;
    setTick((t) => t + 1);
  };
  const getOptions = () => options;

  // Commit the current rows and close the popup.
  const close = () => props.stopEditing && props.stopEditing();

  const ROLE_TITLE = { preparer: "Preparer", reviewer: "Reviewer", approver: "Approver" };

  return (
    <div
      style={{
        minWidth: 440,
        maxWidth: 600,
        background: "#ffffff",
        border: "1px solid #d5dbdb",
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(0, 7, 22, 0.18)",
        padding: 16,
        // Sit above the grid rows so nothing bleeds through the popup.
        zIndex: 1,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <Box variant="h4" padding="n">
          {ROLE_TITLE[role] || "Edit"}
        </Box>
        <Button variant="icon" iconName="close" ariaLabel="Close" onClick={close} />
      </div>

      <CustomMultiHybridInputAndSelect
        field={role}
        selectKey="type"
        inputKey="alias"
        placeholder="alias / group"
        selectHint="USER / LDAP"
        getValues={getValues}
        updateValue={updateValue}
        getOptions={getOptions}
        duplicatesNotAllowed={true}
        loginOptions={suggestedLogins}
        getLoginOptions={getSuggestedLoginsCaller}
        aliasesLoading={aliasesLoading}
      />

      <div style={{ marginTop: 12, textAlign: "right" }}>
        <Button variant="primary" onClick={close}>
          Done
        </Button>
      </div>
    </div>
  );
});

export default RoleAliasCellEditor;
